import { Icon, Text } from '@mxenabled/mxui'

import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { AccountIcon, AccountType, PropertyType } from '../../core'
import type { Account, AccountsCopy } from '../../core'

import { PropertyIcon } from '../../core/types/Account'
import {
  getAccountStartingBalance,
  isAccountLiability,
  isManual,
  usesCreditLimitField,
  usesInterestRateField,
  usesOriginalBalanceField,
} from '../../core/utils/AccountUtils'
import { FieldType } from '../constants/Accounts'
import { fromUnixUTC } from '../../core/utils/DateUtil'
import {
  formatCurrency,
  formatOrdinal,
  formatPercentage,
  isValidNumber,
} from '../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../core/utils/Localization'

type SelectOption = {
  label: string
  value: number
  icon: React.ReactElement
}

export type AccountField = {
  event?: string
  label: string
  minAmount?: number
  options?: SelectOption[]
  name: keyof Account
  placeholder?: string
  type?: string
  validation?: (val: any) => string
  valueFormatter: (val: any) => any
}

export const getAccountDetailFields = (account: Account, copy: AccountsCopy) => {
  const fields: AccountField[] = [
    {
      event: 'account_details_click_account_name',
      label: copy.account_name,
      name: 'user_name',
      placeholder: buildDynamicCopy(copy.enter_value, copy.account_name) as string,
      type: FieldType.Input,
      validation: (val: string) =>
        val ? '' : (buildDynamicCopy(copy.field_is_required, copy.account_name) as string),
      valueFormatter: (val) => val,
    },
  ]

  if (isManual(account)) {
    fields.unshift({
      label: copy.account_balance,
      name: 'balance',
      placeholder: buildDynamicCopy(copy.enter_value, copy.account_balance) as string,
      type: FieldType.Currency,
      valueFormatter: (val) => formatCurrency(val, '0,0.00'),
    })

    const accountTypeOptions = Object.keys(AccountType)
      .filter((key) => Number(key))
      .map((value) => ({
        label: copy.account_types[value],
        value: Number(value),
        icon: <Icon name={AccountIcon[value]} />,
      }))

    fields.push({
      label: copy.account_type,
      name: 'account_type',
      options: accountTypeOptions,
      placeholder: buildDynamicCopy(copy.select_value, copy.account_type) as string,
      type: FieldType.Select,
      valueFormatter: (val) => accountTypeOptions.find((option) => option.value === val)?.label,
    })
  }

  if (account.account_type === AccountType.PROPERTY) {
    const propertyTypeOptions = Object.keys(PropertyType)
      .filter((key) => Number(key))
      .map((value) => ({
        label: copy.property_types[value],
        value: Number(value),
        icon: <Icon name={PropertyIcon[value]} />,
      }))

    fields.push({
      label: copy.property_type,
      name: 'property_type',
      options: propertyTypeOptions,
      placeholder: buildDynamicCopy(copy.select_value, copy.property_type) as string,
      type: FieldType.Select,
      valueFormatter: (val) => propertyTypeOptions.find((option) => option.value === val)?.label,
    })
  }

  if (usesInterestRateField(account)) {
    let label = copy.interest_rate
    let fieldName = 'interest_rate' as keyof Account

    if (!account.is_manual) {
      if (typeof account.apr === 'number') {
        label = copy.apr
        fieldName = 'apr'
      }
      if (typeof account.apy === 'number') {
        label = copy.apy
        fieldName = 'apy'
      }
    }

    fields.push({
      event: 'account_details_click_interest_rate',
      label,
      name: fieldName,
      placeholder: buildDynamicCopy(copy.enter_value, label) as string,
      type: FieldType.Input,
      validation: (val) => (!isValidNumber(val) ? copy.error_amount_is_invalid : ''),
      valueFormatter: (val) => (isValidNumber(val) ? formatPercentage(val / 100) : val),
    })
  }

  if (isAccountLiability(account)) {
    fields.push({
      label: copy.minimum_payment,
      name: 'minimum_payment',
      placeholder: buildDynamicCopy(copy.enter_value, copy.minimum_payment) as string,
      type: FieldType.Currency,
      valueFormatter: (val) => formatCurrency(val, '0,0.00'),
    })

    if (account.payment_due_at) {
      fields.push({
        label: copy.next_payment,
        name: 'payment_due_at',
        type: FieldType.Date,
        valueFormatter: (val) => {
          // accounts for Unix and DateTime values
          const dateTimeStamp = typeof val === 'number' ? fromUnixUTC(val) : val
          return formatDate(dateTimeStamp, DATE_FORMATS_INTL.MED_MONTH_SHORT_DAY_FULL_YEAR)
        },
      })
    } else {
      fields.push({
        label: copy.day_payment_due,
        name: 'day_payment_is_due',
        placeholder: buildDynamicCopy(copy.enter_value, copy.day_payment_due) as string,
        type: FieldType.Date,
        valueFormatter: (val) =>
          buildDynamicCopy(
            copy.day_of_every_month,
            formatOrdinal(val <= 31 ? val : new Date(val).getDate()),
          ) as string,
      })
    }
  }

  if (usesCreditLimitField(account)) {
    fields.push({
      label: copy.credit_limit,
      name: 'credit_limit',
      placeholder: buildDynamicCopy(copy.enter_value, copy.credit_limit) as string,
      type: FieldType.Currency,
      valueFormatter: (val) => formatCurrency(val, '0,0.00'),
    })
  }

  if (usesOriginalBalanceField(account)) {
    fields.push({
      label: copy.original_balance,
      minAmount: 1,
      name: 'original_balance',
      placeholder: buildDynamicCopy(copy.enter_value, copy.original_balance) as string,
      type: FieldType.Currency,
      valueFormatter: (val) => formatCurrency(val, '0,0.00'),
    })
  }

  return fields
}
export const getAccountBalanceDetails = (
  account: Account,
  copy: AccountsCopy,
  showAvailableBalance: boolean,
) => {
  let firstSubtitle = ''
  let firstValue = null
  let secondSubtitle = null
  let secondValue = null
  let hasAvailableBalance = false

  switch (account.account_type) {
    // checking or savings
    case 1:
    case 2: {
      // some savings accounts only have balance, and not available_balance
      if (account.available_balance != null && showAvailableBalance) {
        firstSubtitle = copy.available_balance
        firstValue = getAccountStartingBalance(account, showAvailableBalance)
        secondSubtitle = copy.current_balance
        secondValue = formatCurrency(account.balance, '0,0.00')
        hasAvailableBalance = true
      } else {
        firstSubtitle = copy.current_balance
        firstValue = getAccountStartingBalance(account, showAvailableBalance)
      }
      break
    }
    default: {
      firstSubtitle = copy.current_balance
      firstValue = account.balance
      if (account.minimum_payment && account.payment_due_at) {
        const dateTimeStamp = fromUnixUTC(account.payment_due_at)
        secondSubtitle = (
          <Text component="strong" variant="caption">
            {
              buildDynamicCopy(
                copy.payment_due_on,
                formatCurrency(account.minimum_payment, '0,0.00'),
                formatDate(dateTimeStamp, DATE_FORMATS_INTL.FULL_MONTH_DAY),
              ) as string
            }
          </Text>
        )
      } else if (account.credit_limit) {
        secondSubtitle = copy.available_credit
        secondValue = formatCurrency(account.credit_limit - (account?.balance ?? 0), '0,0.00')
      }
    }
  }

  return {
    firstSubtitle,
    firstValue,
    secondSubtitle,
    secondValue,
    hasAvailableBalance,
  }
}

export const getClosedAccountBalanceTotal = (
  accounts: Account[],
  copy: AccountsCopy,
  showAvailableBalance: boolean,
) => {
  const total = accounts.reduce((sum, account) => {
    const details = getAccountBalanceDetails(account, copy, showAvailableBalance)
    const firstValue = details.firstValue || 0
    return sum + firstValue
  }, 0)

  return total
}
